import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, Plus, Shield, Trash2, Globe } from "lucide-react";
import { useDeviceBrowserPolicies } from "@/hooks/useDeviceBrowserPolicies";
import { useBlueJayRulesRead } from "@/hooks/useBlueJayRulesRead";
import { DEFAULT_BLUEJAY_CONFIG } from "@/config/nsfwRulePresets";

interface DeviceBrowserWhitelistContentProps {
  deviceId: string;
}

export const DeviceBrowserWhitelistContent = ({ deviceId }: DeviceBrowserWhitelistContentProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [newUrl, setNewUrl] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);

  const { updateBrowserPolicy } = useDeviceBrowserPolicies(deviceId);

  // Read blueJayRules from external endpoint
  const { blueJayRules, isLoading: isLoadingRules, refetch } = useBlueJayRulesRead(deviceId);

  // Refetch data on mount to ensure fresh data
  useEffect(() => {
    refetch();
  }, [refetch]);

  // Get current values from blueJayRules
  const currentAllowlist = blueJayRules && typeof blueJayRules === "object" 
    ? blueJayRules.allowlist 
    : [];
  const currentBlocklist = blueJayRules && typeof blueJayRules === "object" 
    ? blueJayRules.blocklist 
    : [];
  const currentCategories = blueJayRules && typeof blueJayRules === "object" 
    ? blueJayRules.categories 
    : [];
  const currentConfig = blueJayRules && typeof blueJayRules === "object" 
    ? blueJayRules.config 
    : DEFAULT_BLUEJAY_CONFIG;

  const handleAddUrl = async () => {
    if (!newUrl.trim()) return;

    // Normalize URL to lowercase for case-sensitive matching
    const normalizedUrl = newUrl.trim().toLowerCase();

    // Check if URL is already in blocklist
    if (currentBlocklist.includes(normalizedUrl)) {
      return; // Cannot add to allowlist if in blocklist
    }

    // Check if already in allowlist
    if (currentAllowlist.includes(normalizedUrl)) {
      return;
    }

    setIsUpdating(true);
    try {
      await updateBrowserPolicy(
        { allowlist: [...currentAllowlist, normalizedUrl] },
        {
          categories: currentCategories,
          allowlist: currentAllowlist,
          blocklist: currentBlocklist,
          nsfwLevel: currentConfig.nsfwLevel,
          human: currentConfig.human,
          accuracy: currentConfig.accuracy,
        },
        refetch // Pass refetch as onSuccess callback
      );
      setNewUrl("");
    } catch (error) {
      console.error("Error adding URL to allowlist:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRemoveUrl = async (url: string) => {
    setIsUpdating(true);
    try {
      await updateBrowserPolicy(
        { allowlist: currentAllowlist.filter((u: string) => u !== url) },
        {
          categories: currentCategories,
          allowlist: currentAllowlist,
          blocklist: currentBlocklist,
          nsfwLevel: currentConfig.nsfwLevel,
          human: currentConfig.human,
          accuracy: currentConfig.accuracy,
        },
        refetch // Pass refetch as onSuccess callback
      );
    } catch (error) {
      console.error("Error removing URL from allowlist:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const filteredSites = currentAllowlist.filter((url: string) =>
    url.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Only show loading skeleton while actively loading
  // Once loading is complete, show content even if blueJayRules is null/false
  if (isLoadingRules) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-medium">Browser Whitelist</h3>
        <p className="text-gray-600">Websites explicitly allowed for this device</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Add Website to Whitelist</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input
              placeholder="Enter website URL (e.g., example.com)"
              value={newUrl}
              onChange={(e) => setNewUrl(e.target.value)}
              className="flex-1"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleAddUrl();
                }
              }}
            />
            <Button onClick={handleAddUrl} disabled={isUpdating || !newUrl.trim()}>
              <Plus className="w-4 h-4 mr-1" />
              {isUpdating ? "Adding..." : "Add"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
        <Input
          placeholder="Search whitelisted websites..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      <div>
        <h4 className="font-medium mb-3">Current Browser Whitelist</h4>
        {filteredSites.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-8">
              <Shield className="w-8 h-8 text-gray-400 mb-2" />
              <p className="text-sm text-gray-500">
                {searchQuery ? "No websites found matching your search" : "No websites in whitelist"}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {filteredSites.map((url: string, idx: number) => (
              <div key={idx} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-blue-500" />
                  <div>
                    <p className="font-medium text-sm">{url}</p>
                    <p className="text-xs text-gray-500">Website URL</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="default">Allowed</Badge>
                  <Button size="sm" variant="outline" onClick={() => handleRemoveUrl(url)} disabled={isUpdating}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
